"use client";

import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, AlertTriangle, Printer, Send, Mail } from "lucide-react";

/**
 * Payment status badge component
 * Shows Paid, Unpaid, or Partially Paid status based on balance and total amount
 */
export const PaymentStatusBadge: React.FC<{
  balance?: number;
  total?: number;
}> = ({ balance, total }) => {
  const badge = useMemo(() => {
    if (balance === undefined || total === undefined) {
      return (
        <Badge variant="secondary" className="gap-1">
          <AlertTriangle className="h-3 w-3" />
          Unknown
        </Badge>
      );
    }

    if (balance === 0) {
      return (
        <Badge variant="default" className="bg-green-500 gap-1">
          <CheckCircle className="h-3 w-3" />
          Paid
        </Badge>
      );
    } else if (balance === total) {
      return (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="h-3 w-3" />
          Unpaid
        </Badge>
      );
    } else {
      return (
        <Badge variant="outline" className="gap-1">
          <AlertTriangle className="h-3 w-3" />
          Partially Paid
        </Badge>
      );
    }
  }, [balance, total]);

  return badge;
};

/**
 * Print status badge component
 * Shows print status: Needs Printing, Not Set, or Printed
 */
export const PrintStatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const badge = useMemo(() => {
    switch (status) {
      case "NeedToPrint":
        return (
          <Badge variant="outline" className="gap-1">
            <Printer className="h-3 w-3" />
            Needs Printing
          </Badge>
        );
      case "NotSet":
        return (
          <Badge variant="secondary" className="gap-1">
            Not Set
          </Badge>
        );
      default:
        return (
          <Badge variant="default" className="gap-1">
            <CheckCircle className="h-3 w-3" />
            Printed
          </Badge>
        );
    }
  }, [status]);

  return badge;
};

/**
 * Email status badge component
 * Shows email delivery status: Sent, Not Sent, or Not Set
 */
export const EmailStatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const badge = useMemo(() => {
    switch (status) {
      case "EmailSent":
        return (
          <Badge variant="default" className="bg-blue-500 gap-1">
            <Send className="h-3 w-3" />
            Sent
          </Badge>
        );
      case "NotSet":
        return (
          <Badge variant="secondary" className="gap-1">
            Not Set
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="gap-1">
            <Mail className="h-3 w-3" />
            Not Sent
          </Badge>
        );
    }
  }, [status]);

  return badge;
};
